import cn from 'classnames';
import React, { PropsWithChildren, useState } from 'react';
import { ArrayOfReactElement } from '../common/Types';

type StackViewProps = PropsWithChildren<{
  header: React.ReactElement;
}>;

export const StackView = React.forwardRef<HTMLDivElement, StackViewProps>(
  (props, ref) => {
    const { children, header } = props;
    return (
      <div ref={ref}>
        {header}
        <div className="stack-view">{children}</div>
      </div>
    );
  }
);

export const StackHeader = React.forwardRef<
  HTMLHeadingElement,
  PropsWithChildren<{}>
>((props, ref) => {
  return (
    <h4 ref={ref} className="stack-header">
      <span className="stack-title">{props.children}</span>
    </h4>
  );
});

interface StackBlockProps {
  expandable?: boolean;
  expanded?: boolean;
  label: React.ReactNode;
  content?: React.ReactNode;
  stackChildren?: ArrayOfReactElement<StackBlockProps>;
  viewLevel?: number;
  viewSetsize?: number;
  viewPosinset?: number;
}

export const StackBlock = React.forwardRef<HTMLDivElement, StackBlockProps>(
  (props, ref) => {
    const {
      content,
      expandable,
      label,
      stackChildren,
      viewLevel,
      viewPosinset,
      viewSetsize
    } = props;
    const [expanded, setExpanded] = useState(props.expanded || false);
    const classNames = cn('stack-block', {
      'stack-block-expandable': expandable,
      'stack-block-expanded': expanded
    });

    const toggleBlock = () => {
      if (expandable) {
        setExpanded(prevExpanded => !prevExpanded);
      }
    };

    return (
      <div ref={ref} className={classNames}>
        <div
          className="stack-block-label"
          role={expandable ? 'button' : 'none'}
          tabIndex={0}
          onClick={toggleBlock}
          aria-expanded={expanded}
          aria-level={viewLevel}
          aria-setsize={viewSetsize}
          aria-posinset={viewPosinset}>
          <div className="stack-view-key">
            <div>{label}</div>
          </div>
          <div className="stack-block-content">
            <div>{content}</div>
          </div>
        </div>
        <div className="stack-children">
          <div role="region" style={{ height: expanded ? 'auto' : '0px' }}>
            {stackChildren}
          </div>
        </div>
      </div>
    );
  }
);
